import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'react-feather';
import { ChoiceCards, EtlBanner, EtlLoading } from '../EtlPages';

// Import PDF.js
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker to use local file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFPageSelector = ({ file, onPageSelection, initialSelectedPages = null }) => {
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [pageRange, setPageRange] = useState({ start: '', end: '' });
  const [selectionMode, setSelectionMode] = useState('all'); // 'all', 'range', 'custom', 'keyword'
  const [currentPreviewPage, setCurrentPreviewPage] = useState(1);
  const [pageCanvas, setPageCanvas] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfDocument, setPdfDocument] = useState(null);

  // Keyword-based selection state
  const [keywordConfigs, setKeywordConfigs] = useState([{ id: 1, keyword: '', pageCount: 1 }]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [pdfText, setPdfText] = useState([]);
  const [nextConfigId, setNextConfigId] = useState(2);

  useEffect(() => {
    if (file) {

      loadPDF();
    } else {

    }
  }, [file]);

  useEffect(() => {
    if (initialSelectedPages) {
      if (initialSelectedPages.mode === 'all') {
        setSelectionMode('all');
      } else if (initialSelectedPages.mode === 'range') {
        setSelectionMode('range');
        setPageRange(initialSelectedPages.range);
      } else if (initialSelectedPages.mode === 'custom') {
        setSelectionMode('custom');
        setSelectedPages(new Set(initialSelectedPages.pages));
      } else if (initialSelectedPages.mode === 'keyword') {
        setSelectionMode('keyword');
        // Support both single keyword (legacy) and multiple keywords (new)
        if (initialSelectedPages.keywordConfigs) {
          setKeywordConfigs(initialSelectedPages.keywordConfigs);
          setNextConfigId(Math.max(...initialSelectedPages.keywordConfigs.map(c => c.id)) + 1);
        } else if (initialSelectedPages.keyword) {
          // Legacy single keyword format
          setKeywordConfigs([{ id: 1, keyword: initialSelectedPages.keyword, pageCount: initialSelectedPages.pageCount || 1 }]);
        }
        if (initialSelectedPages.pages) {
          setSelectedPages(new Set(initialSelectedPages.pages));
        }
      }
    }
  }, [initialSelectedPages]);

  const loadPDF = async () => {
    try {
      setIsLoading(true);

      const fileUrl = URL.createObjectURL(file);

      const loadingTask = pdfjsLib.getDocument(fileUrl);

      const pdf = await loadingTask.promise;

      setPdfDocument(pdf);
      setTotalPages(pdf.numPages);

      // Extract text from all pages for keyword search
      await extractPDFText(pdf);

      // Initialize with all pages selected by default
      if (!initialSelectedPages) {
        const allPages = new Set();
        for (let i = 1; i <= pdf.numPages; i++) {
          allPages.add(i);
        }
        setSelectedPages(allPages);
        setSelectionMode('all'); // Ensure 'all' is selected by default
        // Notify parent with default selection
        notifyPageSelection('all', Array.from(allPages));

      } else {

      }

      // Load first page preview
      if (pdf.numPages > 0) {
        await loadPagePreview(pdf, 1);
      }

      setIsLoading(false);
      
      // Clean up the URL object
      URL.revokeObjectURL(fileUrl);
      
    } catch (error) {
      console.error('Error loading PDF:', error);
      console.error('Error details:', error.message, error.stack);
      setIsLoading(false);
    }
  };

  const loadPagePreview = async (pdf, pageNum) => {
    try {

      const page = await pdf.getPage(pageNum);

      const scale = 0.5;
      const viewport = page.getViewport({ scale });

      const canvas = document.getElementById('pdf-preview-canvas');
      if (!canvas) {
        console.error('Canvas element not found with ID: pdf-preview-canvas');
        return;
      }

      const dpr = window.devicePixelRatio || 1;
      const context = canvas.getContext('2d');

      // Set canvas internal resolution for high-DPI displays
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);

      // Set CSS display size
      canvas.style.width = Math.floor(viewport.width) + 'px';
      canvas.style.height = Math.floor(viewport.height) + 'px';

      // Scale context for DPR
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;

      setPageCanvas(canvas);
    } catch (error) {
      console.error('Error loading page preview:', error);
      console.error('Preview error details:', error.message, error.stack);
    }
  };

  const extractPDFText = async (pdf) => {
    try {
      const textByPage = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        textByPage.push({ pageNum: i, text: pageText });
      }
      setPdfText(textByPage);
      console.log('Extracted text from all pages');
    } catch (error) {
      console.error('Error extracting PDF text:', error);
    }
  };

  const searchKeywordInPDF = () => {
    // Filter out empty keywords
    const validConfigs = keywordConfigs.filter(config => config.keyword && config.keyword.trim());

    if (!validConfigs.length || !pdfText.length) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const results = [];
    const allPagesToSelect = new Set();

    // Search for each keyword configuration
    validConfigs.forEach(config => {
      const lowerKeyword = config.keyword.toLowerCase();
      const matchedPages = [];

      // Find ALL occurrences of this keyword
      pdfText.forEach(({ pageNum, text }) => {
        if (text.toLowerCase().includes(lowerKeyword)) {
          matchedPages.push(pageNum);

          // Calculate pages to select for this keyword match
          for (let i = 0; i < config.pageCount; i++) {
            const targetPage = pageNum + i;
            if (targetPage <= totalPages) {
              allPagesToSelect.add(targetPage);
            }
          }
        }
      });

      // Add results for this keyword
      if (matchedPages.length > 0) {
        results.push({
          keyword: config.keyword,
          matchedPages: matchedPages,
          pageCount: config.pageCount,
          configId: config.id
        });
      } else {
        // If keyword not found, add to results with no match
        results.push({
          keyword: config.keyword,
          matchedPages: [],
          pageCount: config.pageCount,
          configId: config.id
        });
      }
    });

    setSearchResults(results);
    setIsSearching(false);

    // Update selected pages
    setSelectedPages(allPagesToSelect);
    notifyPageSelection('keyword', Array.from(allPagesToSelect).sort((a, b) => a - b), null, { keywordConfigs: validConfigs });
  };

  const handleModeChange = (mode) => {
    setSelectionMode(mode);

    // Don't process if PDF isn't loaded yet
    if (totalPages === 0) {
      console.warn('PDF not loaded yet, cannot change selection mode');
      return;
    }

    if (mode === 'all') {
      const allPages = new Set();
      for (let i = 1; i <= totalPages; i++) {
        allPages.add(i);
      }
      setSelectedPages(allPages);
      notifyPageSelection(mode, Array.from(allPages));

    } else if (mode === 'range') {
      // If range inputs are empty, default to all pages
      const start = pageRange.start || 1;
      const end = pageRange.end || totalPages;
      setPageRange({ start: start.toString(), end: end.toString() });
      updateRangeSelection(start, end);
    } else if (mode === 'keyword') {
      // Keyword mode - search will trigger page selection
      setSearchResults([]);
      const validConfigs = keywordConfigs.filter(config => config.keyword && config.keyword.trim());
      notifyPageSelection(mode, Array.from(selectedPages), null, { keywordConfigs: validConfigs });
    } else {
      // Custom mode - keep current selection or default to empty
      notifyPageSelection(mode, Array.from(selectedPages));

    }
  };

  const updateRangeSelection = (start, end) => {
    const startNum = parseInt(start) || 1;
    const endNum = parseInt(end) || totalPages;
    

    if (startNum <= endNum && startNum >= 1 && endNum <= totalPages) {
      const rangePages = new Set();
      for (let i = startNum; i <= endNum; i++) {
        rangePages.add(i);
      }
      setSelectedPages(rangePages);
      notifyPageSelection('range', Array.from(rangePages), { start: startNum, end: endNum });

    } else {
      console.warn('Invalid range:', { startNum, endNum, totalPages });
    }
  };

  const togglePageSelection = (pageNum) => {
    const newSelection = new Set(selectedPages);
    if (newSelection.has(pageNum)) {
      newSelection.delete(pageNum);
    } else {
      newSelection.add(pageNum);
    }
    setSelectedPages(newSelection);
    notifyPageSelection('custom', Array.from(newSelection));
  };

  const notifyPageSelection = (mode, pages, range = null, keywordConfig = null) => {
    const selectionData = {
      mode,
      pages: pages.sort((a, b) => a - b),
      range,
      totalPages
    };

    // Add keyword configuration if provided
    if (keywordConfig) {
      if (keywordConfig.keywordConfigs) {
        // Multiple keywords (new format)
        selectionData.keywordConfigs = keywordConfig.keywordConfigs;
      } else if (keywordConfig.keyword) {
        // Legacy single keyword support
        selectionData.keyword = keywordConfig.keyword;
        selectionData.pageCount = keywordConfig.pageCount;
      }
    }

    if (onPageSelection) {
      onPageSelection(selectionData);
    } else {
      console.warn('No onPageSelection callback provided');
    }
  };

  const handleRangeChange = (field, value) => {
    const newRange = { ...pageRange, [field]: value };
    setPageRange(newRange);
    
    if (selectionMode === 'range') {
      updateRangeSelection(newRange.start, newRange.end);
    }
  };

  const navigatePreview = (direction) => {
    const newPage = currentPreviewPage + direction;
    if (newPage >= 1 && newPage <= totalPages && pdfDocument) {
      setCurrentPreviewPage(newPage);
      loadPagePreview(pdfDocument, newPage);
    }
  };

  if (isLoading) return <EtlLoading>Reading the PDF…</EtlLoading>;

  const keywordsEntered = keywordConfigs.filter((c) => c.keyword.trim()).length;

  return (
    <>
      <ChoiceCards
        ariaLabel="Page selection mode"
        value={selectionMode}
        onChange={handleModeChange}
        options={[
          { value: 'all', title: 'Every page', sub: `Read all ${totalPages} pages.`, disabled: totalPages === 0 },
          { value: 'range', title: 'A page range', sub: 'Read a contiguous block, e.g. pages 3–7.', disabled: totalPages === 0 },
          { value: 'custom', title: 'Specific pages', sub: 'Pick pages by hand.', disabled: totalPages === 0 },
          {
            value: 'keyword',
            title: 'Wherever a keyword appears',
            sub: 'Survives a report whose section moves page to page — the usual reason a night audit breaks.',
            disabled: totalPages === 0,
          },
        ]}
      />

      <div className="cp-help" style={{ marginTop: 10 }}>
        {selectedPages.size} of {totalPages} pages selected.
      </div>

      {selectionMode === 'range' ? (
        <div className="ep-panel" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="number" min="1" max={totalPages} className="cp-input" style={{ width: 90 }}
            value={pageRange.start} onChange={(e) => handleRangeChange('start', e.target.value)}
            placeholder="From" aria-label="First page"
          />
          <span className="cp-help">to</span>
          <input
            type="number" min="1" max={totalPages} className="cp-input" style={{ width: 90 }}
            value={pageRange.end} onChange={(e) => handleRangeChange('end', e.target.value)}
            placeholder="To" aria-label="Last page"
          />
        </div>
      ) : null}

      {selectionMode === 'keyword' ? (
        <div className="ep-panel" style={{ marginTop: 12 }}>
          <div className="ep-section-head">
            <h3>Keywords</h3>
            <p>Each match selects that page plus the pages after it.</p>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="cp-btn cp-sm"
              onClick={() => {
                setKeywordConfigs((prev) => [...prev, { id: nextConfigId, keyword: '', pageCount: 1 }]);
                setNextConfigId((prev) => prev + 1);
              }}
            >
              <Plus size={13} /> Add
            </button>
          </div>

          <div style={{ display: 'grid', gap: 6 }}>
            {keywordConfigs.map((config, idx) => (
              <div key={config.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="cp-code" style={{ width: 16 }}>{idx + 1}</span>
                <input
                  type="text"
                  className="cp-input"
                  style={{ flex: 1 }}
                  value={config.keyword}
                  placeholder="Text that appears on the page you want"
                  aria-label={`Keyword ${idx + 1}`}
                  onChange={(e) => {
                    const next = e.target.value;
                    setKeywordConfigs((prev) => prev.map((c, i) => (i === idx ? { ...c, keyword: next } : c)));
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && searchKeywordInPDF()}
                />
                <input
                  type="number" min="1" max={totalPages}
                  className="cp-input" style={{ width: 74 }}
                  value={config.pageCount}
                  title="How many pages to take, starting at the match"
                  aria-label={`Pages to take for keyword ${idx + 1}`}
                  onChange={(e) => {
                    const count = parseInt(e.target.value, 10) || 1;
                    setKeywordConfigs((prev) => prev.map((c, i) => (i === idx ? { ...c, pageCount: count } : c)));
                  }}
                />
                <span className="cp-help" style={{ width: 34 }}>pg{config.pageCount > 1 ? 's' : ''}</span>
                {keywordConfigs.length > 1 ? (
                  <button
                    type="button"
                    className="cp-btn cp-sm cp-icon cp-ghost"
                    aria-label={`Remove keyword ${idx + 1}`}
                    onClick={() => setKeywordConfigs((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <Trash2 size={13} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>

          <button
            type="button"
            className="cp-btn cp-primary"
            style={{ marginTop: 10 }}
            onClick={searchKeywordInPDF}
            disabled={!keywordsEntered || isSearching}
          >
            {isSearching ? 'Searching…' : `Search ${keywordsEntered} keyword${keywordsEntered === 1 ? '' : 's'}`}
          </button>

          {searchResults.length > 0 ? (
            <div style={{ marginTop: 12 }}>
              <EtlBanner tone={searchResults.some((r) => r.matchedPages?.length) ? 'ok' : 'warn'} title="Search results">
                <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                  {searchResults.map((result, idx) => (
                    <li key={idx}>
                      {result.matchedPages?.length
                        ? `"${result.keyword}" on page${result.matchedPages.length === 1 ? '' : 's'} ${result.matchedPages.join(', ')}${result.pageCount > 1 ? ` (+${result.pageCount - 1} after each)` : ''}`
                        : `"${result.keyword}" not found`}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 6 }}>
                  Selected: {Array.from(selectedPages).sort((a, b) => a - b).join(', ') || 'none'}
                </div>
              </EtlBanner>
            </div>
          ) : null}
        </div>
      ) : null}

      {selectionMode === 'custom' && totalPages <= 50 ? (
        <div className="ep-panel" style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
          <div>
            <div className="cp-label" style={{ marginBottom: 6 }}>Pages</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(46px, 1fr))', gap: 4, maxHeight: '15rem', overflowY: 'auto' }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  aria-pressed={selectedPages.has(pageNum)}
                  className={`cp-chip cp-mono${selectedPages.has(pageNum) ? ' cp-active' : ''}`}
                  style={{ justifyContent: 'center', padding: 0 }}
                  onClick={() => togglePageSelection(pageNum)}
                >
                  {pageNum}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span className="cp-label">Page {currentPreviewPage}</span>
              <div style={{ flex: 1 }} />
              <button type="button" className="cp-btn cp-sm cp-icon cp-ghost" aria-label="Previous page"
                      onClick={() => navigatePreview(-1)} disabled={currentPreviewPage === 1}>
                <ChevronLeft size={13} />
              </button>
              <button type="button" className="cp-btn cp-sm cp-icon cp-ghost" aria-label="Next page"
                      onClick={() => navigatePreview(1)} disabled={currentPreviewPage === totalPages}>
                <ChevronRight size={13} />
              </button>
            </div>
            <div style={{ border: '1px solid var(--lodge-onyx-100)', borderRadius: 8, overflow: 'hidden', background: 'var(--lodge-white)' }}>
              <canvas id="pdf-preview-canvas" style={{ width: '100%', display: 'block' }} />
            </div>
          </div>
        </div>
      ) : null}

      {selectionMode === 'custom' && totalPages > 50 ? (
        <div className="ep-panel" style={{ marginTop: 12 }}>
          <div className="cp-help" style={{ marginBottom: 6 }}>
            {totalPages} pages is too many to click through — list them instead, e.g. 1,3,5-10,15.
          </div>
          <textarea
            className="cp-input cp-mono"
            rows={3}
            placeholder="1,2,3,5-10,15,20-25"
            aria-label="Page numbers"
            onChange={(e) => {
              const pages = parsePageSelection(e.target.value, totalPages);
              setSelectedPages(new Set(pages));
              notifyPageSelection('custom', pages);
            }}
          />
        </div>
      ) : null}
    </>
  );
};

// Helper function to parse page selection string
const parsePageSelection = (input, maxPages) => {
  const pages = new Set();
  const parts = input.split(',').map(s => s.trim());
  
  for (const part of parts) {
    if (part.includes('-')) {
      const [start, end] = part.split('-').map(s => parseInt(s.trim()));
      if (!isNaN(start) && !isNaN(end)) {
        for (let i = Math.max(1, start); i <= Math.min(maxPages, end); i++) {
          pages.add(i);
        }
      }
    } else {
      const page = parseInt(part);
      if (!isNaN(page) && page >= 1 && page <= maxPages) {
        pages.add(page);
      }
    }
  }
  
  return Array.from(pages).sort((a, b) => a - b);
};

export default PDFPageSelector;